/**
 * MFA-LINK-1b (8-Oct-2026): public enrolment page for an ADMIN-ISSUED link (?token=...).
 * No session: the token is the credential. start -> QR + secret + backup codes; verify -> MFA on,
 * token consumed, every session revoked; then the holder signs in normally.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { CheckCircle2, AlertCircle } from "lucide-react";

import { Card, CardHeader, CardTitle, CardSubtitle, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

type StartResponse = { status: string; otpauth_uri: string; secret_base32: string; backup_codes: string[] };

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) throw new Error(data.error ?? "http_" + res.status);
  return data;
}

export default function MfaEnrollPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [setup, setSetup] = useState<StartResponse | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true; // StrictMode double-invocation guard: start replaces the secret
    post<StartResponse>("/api/auth/mfa/enroll/start", { token })
      .then(async (r) => { setSetup(r); setQr(await QRCode.toDataURL(r.otpauth_uri, { width: 220, margin: 1 })); })
      .catch((e: Error) => setError(e.message));
  }, [token]);

  async function verify() {
    setBusy(true); setError(null);
    try { await post("/api/auth/mfa/enroll/verify", { token, code }); setDone(true); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  if (!token || (error && !setup)) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50"><AlertCircle className="h-6 w-6 text-danger-600" /></div>
          <CardTitle>{t("errors.invalid_or_expired_token")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_enroll.invalid_hint")}</CardSubtitle>
        </CardHeader>
      </Card>
    );
  }

  if (done) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50"><CheckCircle2 className="h-6 w-6 text-success-600" /></div>
          <CardTitle>{t("auth.mfa_enroll.done_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_enroll.done_subtitle")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link to="/login" className="text-[14px] font-semibold text-blue-700 hover:underline">{t("auth.reset_password.sign_in_now")}</Link>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.mfa_enroll.title")}</CardTitle>
        <CardSubtitle>{t("auth.mfa_enroll.subtitle")}</CardSubtitle>
      </CardHeader>
      <CardBody>
        {!setup ? (
          <p className="text-[14px] text-zinc-500">...</p>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col items-center gap-3">
              {qr ? <img src={qr} alt="QR" className="h-[220px] w-[220px] rounded-xl ring-1 ring-zinc-200" /> : null}
              <p className="text-[12px] text-zinc-500">{t("auth.mfa_enroll.secret_hint")}</p>
              <code className="select-all rounded-lg bg-zinc-100 px-3 py-1.5 font-mono text-[13px] tracking-wider text-zinc-800">{setup.secret_base32}</code>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-zinc-900">{t("auth.mfa_enroll.backup_title")}</p>
              <p className="mt-1 text-[12px] text-zinc-500">{t("auth.mfa_enroll.backup_hint")}</p>
              <div className="mt-2 grid grid-cols-2 gap-1 rounded-xl bg-zinc-50 p-3 font-mono text-[13px] text-zinc-800">
                {setup.backup_codes.map((c) => <span key={c} className="select-all">{c}</span>)}
              </div>
            </div>
            <div>
              <Input id="enroll-code" label={t("auth.mfa_enroll.code_label")} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" />
              {error ? <Alert tone="danger" description={t("auth.mfa_enroll.code_error")} /> : null}
              <Button className="mt-3 w-full" disabled={busy || code.length !== 6} onClick={() => void verify()}>{t("auth.mfa_enroll.submit")}</Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
